// src/pages/ZeroDayMethodPage.jsx
import React, { useState, useCallback } from 'react';
import PageTransition from '../components/Shared/PageTransition';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import ZeroDayHero from '../components/ZeroDayMethod/ZeroDayHero';
import ZeroDayChatCard from '../components/ZeroDayMethod/ZeroDayChatCard';
import ZeroDayProgressCard from '../components/ZeroDayMethod/ZeroDayProgressCard';
import MusicControlPanel from '../components/Shared/MusicControlPanel';
import GameContainer from '../components/Shared/GameContainer';
import { useBackgroundMusic } from '../hooks/useBackgroundMusic';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';

import './ZeroDayMethodPage.css';

const ZeroDayMethodPage = ({ openModal }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  const { serverInfo, targetIp, isConnectedToServer } = useServerConnection();

  // Target Server Info derived from ServerConnectionContext
  const currentHost = serverInfo?.hostname || serverInfo?.ip || targetIp || '';
  const currentPort = serverInfo?.port || '25565';

  // Pipeline & Agent State
  const [agentState, setAgentState] = useState('idle'); // 'idle' | 'running' | 'paused' | 'completed'
  const isAiRunning = agentState === 'running';
  const music = useBackgroundMusic(isAiRunning);
  const [activeTaskIndex, setActiveTaskIndex] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [progressPercent, setProgressPercent] = useState(0);

  const { showToast } = useNotification();

  const handleResetChat = useCallback(() => {
    setTasks([]);
    setProgressPercent(0);
    setActiveTaskIndex(0);
  }, []);

  const handleStartAgent = useCallback(() => {
    setTasks([]);
    setProgressPercent(0);
    setActiveTaskIndex(0);
  }, []);

  // Real plan generated by AI (SET_PLAN)
  const handlePlanGenerated = useCallback((newPlan) => {
    if (!newPlan || !Array.isArray(newPlan) || newPlan.length === 0) return;
    setTasks(newPlan.map((t, idx) => ({
      id: t.id || idx + 1,
      name: t.title || t.name || `Task ${idx + 1}`,
      desc: t.description || t.desc || '',
      descEn: t.description || t.desc || '',
      status: t.status || (idx === 0 ? 'running' : 'pending'),
      time: '—'
    })));
    setActiveTaskIndex(0);
    setProgressPercent(10);
    showToast(isEn ? 'AI audit plan formulated' : 'План аудита ИИ сформирован');
  }, [isEn, showToast]);

  // Real plan updates by AI (PLAN_UPDATE)
  const handlePlanUpdate = useCallback((updates) => {
    if (!updates || !Array.isArray(updates)) return;
    setTasks(prevTasks => {
      const updated = prevTasks.map(task => {
        const u = updates.find(item => item.id === task.id || item.name === task.name);
        if (u) {
          const newStatus = u.status === 'active' ? 'running' : (u.status || task.status);
          return {
            ...task,
            status: newStatus,
            time: newStatus === 'completed' ? (u.time || '0.8s') : task.time
          };
        }
        return task;
      });

      const completed = updated.filter(t => t.status === 'completed').length;
      const total = updated.length;
      const activeIdx = updated.findIndex(t => t.status === 'running');
      if (activeIdx !== -1) setActiveTaskIndex(activeIdx);

      const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
      setProgressPercent(percent);

      if (completed === total && total > 0) {
        setAgentState('completed');
        showToast(isEn ? 'Zero-Day audit completed!' : 'Zero-Day аудит успешно завершен!');
      }

      return updated;
    });
  }, [isEn, showToast]);

  return (
    <PageTransition>
      <div className="zeroday-page-root">
        <div className="relative z-10 max-w-[1320px] mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-12 sm:pb-16 box-border w-full min-w-0">
          {/* 1. Hero Block with Bebas Neue typography */}
          <ZeroDayHero />

          {/* 2. Shared Server MOTD Card with real serverInfo */}
          <ServerInfoCard className="w-full mx-auto relative z-40 mb-[34px]" />

          {/* Music Control Panel */}
          <MusicControlPanel
            isPlaying={isAiRunning}
            volume={music.volume}
            setVolume={music.setVolume}
            isMuted={music.isMuted}
            setIsMuted={music.setIsMuted}
            currentTime={music.currentTime}
            currentTrack={music.currentTrack}
            lyrics={music.lyrics}
            analyser={music.analyser}
            accentColor="white"
            className="mb-4"
            onNextTrack={music.nextTrack}
          />

          {/* Mini-Games Panel */}
          <GameContainer
            isActive={isAiRunning}
            accentColor="cyan"
            className="mb-8"
          />

          {/* 3. Main Workspace Grid: AI Chat (Left) + AI Progress (Right) */}
          <div className="ai-main-grid">
            <ZeroDayChatCard
              targetHost={currentHost}
              targetPort={currentPort}
              isConnectedToServer={isConnectedToServer}
              agentState={agentState}
              setAgentState={setAgentState}
              activeTaskIndex={activeTaskIndex}
              setActiveTaskIndex={setActiveTaskIndex}
              onPlanGenerated={handlePlanGenerated}
              onPlanUpdate={handlePlanUpdate}
              onResetChat={handleResetChat}
              onStartAgent={handleStartAgent}
              showToast={showToast}
              openModal={openModal}
            />

            <ZeroDayProgressCard
              tasks={tasks}
              activeTaskIndex={activeTaskIndex}
              agentState={agentState}
              progressPercent={progressPercent}
            />
          </div>
        </div>

      </div>
    </PageTransition>
  );
};

export default ZeroDayMethodPage;